import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { X, MapPin, Camera, Send, Loader } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useReports } from '../../hooks/useReports';
import { useProfile } from '../../hooks/useProfile';
import styles from './ReportModal.module.scss';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ReportFormData {
  title: string;
  hazardType: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

const ReportModal: React.FC<ReportModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const { addReport, uploadReportPhoto } = useReports();
  const { incrementReportsCount } = useProfile();
  const [formData, setFormData] = useState<ReportFormData>({
    title: '',
    hazardType: '',
    description: '',
    severity: 'medium'
  });
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Auto-detect location when modal opens
  useEffect(() => {
    if (isOpen && !selectedLocation) {
      getCurrentLocation();
    }
  }, [isOpen]);

  const hazardTypes = [
    { value: '', label: 'Select Hazard Type' },
    { value: 'tsunami', label: 'Tsunami Warning' },
    { value: 'hurricane', label: 'Hurricane/Typhoon' },
    { value: 'high-waves', label: 'High Waves' },
    { value: 'coastal-flooding', label: 'Coastal Flooding' },
    { value: 'storm-surge', label: 'Storm Surge' },
    { value: 'rip-current', label: 'Dangerous Rip Currents' },
    { value: 'oil-spill', label: 'Oil Spill' },
    { value: 'marine-debris', label: 'Marine Debris' },
    { value: 'earthquake', label: 'Earthquake (Coastal)' },
    { value: 'wildfire', label: 'Coastal Wildfire' },
    { value: 'other', label: 'Other' }
  ];

  const severityOptions = [
    { value: 'low', label: 'Low', color: '#10b981', description: 'Minor concern, monitor situation' },
    { value: 'medium', label: 'Medium', color: '#f59e0b', description: 'Moderate risk, exercise caution' },
    { value: 'high', label: 'High', color: '#ef4444', description: 'Significant danger, avoid area' },
    { value: 'critical', label: 'Critical', color: '#dc2626', description: 'Extreme danger, evacuate immediately' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedPhoto(file);
    }
  };

  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setSelectedLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setIsGettingLocation(false);
          toast.success('Location captured successfully!');
        },
        (error) => {
          console.error('Error getting location:', error);
          let errorMessage = 'Failed to get location. Please try again.';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permissions.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
          }
          
          toast.error(errorMessage);
          setIsGettingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser.');
      setIsGettingLocation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      toast.error('Please sign in to submit a report');
      return;
    }
    if (!selectedLocation) {
      toast.error('Please capture your location');
      return;
    }
    if (!formData.title || !formData.hazardType || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      let photoUrl = '';
      if (selectedPhoto) {
        photoUrl = await uploadReportPhoto(selectedPhoto);
      }

      const reportData = {
        title: formData.title,
        description: formData.description,
        hazardType: formData.hazardType,
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lng,
        photoUrl,
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        severity: formData.severity,
        status: 'pending' as const
      };

      await addReport(reportData);
      
      // Increment user's reports count
      await incrementReportsCount();
      
      // Reset form
      setFormData({
        title: '',
        hazardType: '',
        description: '',
        severity: 'medium'
      });
      setSelectedPhoto(null);
      setSelectedLocation(null);
      
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className={styles.modal}
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.header}>
              <h2 className={styles.title}>Report Ocean Hazard</h2>
              <button className={styles.closeButton} onClick={onClose}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="title">Report Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Brief description of the hazard"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="hazardType">Hazard Type *</label>
                <select
                  id="hazardType"
                  name="hazardType"
                  value={formData.hazardType}
                  onChange={handleInputChange}
                  required
                >
                  {hazardTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Severity Level *</label>
                <div className={styles.severityGrid}>
                  {severityOptions.map(option => (
                    <label
                      key={option.value}
                      className={`${styles.severityOption} ${
                        formData.severity === option.value ? styles.selected : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="severity"
                        value={option.value}
                        checked={formData.severity === option.value}
                        onChange={handleInputChange}
                      />
                      <div className={styles.severityContent}>
                        <div 
                          className={styles.severityColor}
                          style={{ backgroundColor: option.color }}
                        ></div>
                        <div>
                          <div className={styles.severityLabel}>{option.label}</div>
                          <div className={styles.severityDescription}>{option.description}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Detailed description of the hazard and current conditions"
                  rows={4}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Location *</label>
                <div className={styles.locationSection}>
                  <button
                    type="button"
                    className={styles.locationButton}
                    onClick={getCurrentLocation}
                    disabled={isGettingLocation}
                  >
                    {isGettingLocation ? (
                      <Loader className={styles.spinning} size={20} />
                    ) : (
                      <MapPin size={20} />
                    )}
                    {isGettingLocation ? 'Getting Location...' : 'Capture Current Location'}
                  </button>
                  {selectedLocation && (
                    <div className={styles.locationDisplay}>
                      <MapPin size={16} />
                      <span>
                        {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="photo">Photo (Optional)</label>
                <div className={styles.photoUpload}>
                  <input
                    type="file"
                    id="photo"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className={styles.photoInput}
                  />
                  <label htmlFor="photo" className={styles.photoLabel}>
                    <Camera size={20} />
                    {selectedPhoto ? selectedPhoto.name : 'Choose Photo'}
                  </label>
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader className={styles.spinning} size={20} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Submit Report
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReportModal;
